/**
 * Manages one LiveKit avatar call: token exchange → room connect → mic publish →
 * avatar video/audio render → live transcript + per-leg latency.
 *
 * Provider-neutral core shared by BOTH consumers — the sysadmin Avatar Lab
 * (`useAvatarLabCall`) and the tenant agent avatar call (`useAgentAvatarCall`).
 * The only thing that differs between them is how a room token is minted and
 * which i18n key set labels the errors/mic; everything else (room lifecycle, mic
 * metering, half-duplex, transcript, latency, teardown, autoplay handling) is
 * identical, so it lives here once. `start(mint)` takes the caller's token-mint
 * thunk; `opts.tPrefix` selects the `avatars.<prefix>.*` key set (`lab` for the
 * sysadmin lab's diagnostic copy, `call` for the tenant's user-facing copy).
 *
 * The PanLuma JWT never touches this layer — the browser receives only a
 * short-lived room token from the mint thunk; the agent gets the JWT via
 * server-side dispatch metadata.
 *
 * Agent-startup failures are surfaced, never silent, with a CAUSE-SPECIFIC
 * message (never one generic string): the dispatched agent must JOIN the room
 * within AGENT_JOIN_TIMEOUT_MS of connect and then PUBLISH its avatar video
 * within AGENT_VIDEO_TIMEOUT_MS of joining.
 *
 * A/V robustness — the three things a caller must be able to trust: HEAR the
 * avatar (audio attached to an in-DOM sink, startAudio() on the gesture, an
 * explicit "Enable audio" fallback), SEE the avatar (permanently-mounted sinks so
 * there is no attach race; `videoReady` only flips on a real decoded frame), and
 * know the MIC is live (the PUBLISHED track is metered; device failures surface
 * as specific translated errors). Every resource is released in teardown().
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Room,
  RoomEvent,
  Track,
  MediaDeviceFailure,
  createAudioAnalyser,
  type LocalAudioTrack,
  type LocalTrackPublication,
  type Participant,
  type RemoteTrack,
  type RemoteTrackPublication,
  type RemoteParticipant,
  type RemoteAudioTrack,
  type RemoteVideoTrack,
  type TranscriptionSegment,
} from 'livekit-client';
import { getErrorMessage, getGenericErrorMessage } from '../../utils/errors';

export type AvatarCallStatus = 'idle' | 'connecting' | 'connected' | 'error';

/**
 * Mints a room token for one call. Returns the LiveKit client URL + short-lived
 * room-scoped participant token. The lab flow hits `POST /lab/token`; the agent
 * flow hits `POST /livekit/sessions`. Throwing surfaces as a translated toast +
 * error state (the thunk owns its own error translation before it throws).
 */
export type MintRoomToken = () => Promise<{ livekit_url: string; token: string }>;

/** How long after room connect the dispatched agent has to join at all. */
export const AGENT_JOIN_TIMEOUT_MS = 10_000;
/**
 * How long after the agent joins it has to publish its avatar video track.
 * Generous on purpose: a cold avatar provider (a Simli character loading, a
 * Beyond Presence session spinning up its render, plus the agent worker retrying
 * a transient vendor failure once) can legitimately take >15s on the first call
 * of a session. Too tight a deadline turns a slow-but-fine start into a spurious
 * "joined but never published video" error; 25s clears that headroom while still
 * bounding a genuine crash to a clear message.
 */
export const AGENT_VIDEO_TIMEOUT_MS = 25_000;
/**
 * Mic-meter sampling period. 20 Hz reads as continuous movement to the eye while
 * costing 20 re-renders/sec — polling per animation frame (60 Hz) would triple
 * that for no perceptible gain.
 */
export const MIC_METER_INTERVAL_MS = 50;
/**
 * Weight of each new sample in the meter's exponential smoothing. Raw RMS from
 * the analyser is jumpy enough to make the bar strobe; this damps it without
 * adding perceptible lag to speech onsets.
 */
const MIC_LEVEL_SMOOTHING = 0.4;

/**
 * The picker's synthetic "use whatever the OS default is" option, and the value
 * the call starts on. It is deliberately NOT a device id: while it is selected the
 * capture is left entirely unconstrained. Mid-call it maps to the browser's own
 * "default" device id, the only way to express "follow the OS default" to an
 * already-published track.
 */
export const SYSTEM_DEFAULT_MIC_ID = 'system-default';
/** The browser's alias for the current OS default input. */
const BROWSER_DEFAULT_DEVICE_ID = 'default';

/**
 * Room data-channel topic the agent worker publishes provider-start failures on.
 * MUST match AVATAR_ERROR_TOPIC in `modules/avatars/agent_worker/agent_errors.py`.
 */
export const AVATAR_ERROR_TOPIC = 'panluma.avatar-error';
/** Provider is out of concurrent-session capacity (HTTP 429 / plan cap). */
export const AVATAR_PROVIDER_AT_CAPACITY = 'AVATAR_PROVIDER_AT_CAPACITY';
/** Any other provider-start failure (bad avatar id, vendor error, timeout). */
export const AVATAR_PROVIDER_START_FAILED = 'AVATAR_PROVIDER_START_FAILED';

/** The structured payload the agent worker publishes on {@link AVATAR_ERROR_TOPIC}. */
interface AvatarProviderErrorPayload {
  code: string;
  message?: string;
  error_id?: string;
  timestamp?: string;
}

/**
 * Decodes an avatar-error data-channel packet. Returns null for anything that
 * isn't a well-formed payload (a truncated/garbage packet), so the caller can
 * fall back to the standard unexpected-error message rather than crash.
 */
function parseAvatarProviderError(payload: Uint8Array): AvatarProviderErrorPayload | null {
  try {
    const parsed: unknown = JSON.parse(new TextDecoder().decode(payload));
    if (parsed && typeof parsed === 'object' && typeof (parsed as { code?: unknown }).code === 'string') {
      return parsed as AvatarProviderErrorPayload;
    }
    return null;
  } catch {
    return null;
  }
}

type AgentFailureKey = 'agentTimeout' | 'agentFailed';
type MicFailureKey = 'micPermissionDenied' | 'micNotFound' | 'micInUse' | 'micFailed';

/** Maps a getUserMedia failure to the specific message the caller can act on. */
function micFailureKey(err: unknown): MicFailureKey {
  switch (MediaDeviceFailure.getFailure(err)) {
    case MediaDeviceFailure.PermissionDenied:
      return 'micPermissionDenied';
    case MediaDeviceFailure.NotFound:
      return 'micNotFound';
    case MediaDeviceFailure.DeviceInUse:
      return 'micInUse';
    default:
      return 'micFailed';
  }
}

/** Who spoke a transcript line: the local participant is the user; any remote is the agent/avatar. */
export type AvatarCallTranscriptSpeaker = 'user' | 'agent';

export interface AvatarCallTranscriptLine {
  id: string;
  text: string;
  final: boolean;
  speaker: AvatarCallTranscriptSpeaker;
}

/** Per-leg latency (ms) for the readout — proxies for the realtime pipeline. */
export interface AvatarCallLatency {
  /** Time to exchange the PanLuma session for a room token + agent dispatch. */
  tokenMs?: number;
  /** Time for the browser to establish the WebRTC room connection. */
  connectMs?: number;
  /** Connected → first avatar video frame (agent spin-up + first render). */
  firstFrameMs?: number;
}

export interface UseAvatarCall {
  status: AvatarCallStatus;
  errorMessage: string | null;
  transcript: AvatarCallTranscriptLine[];
  latency: AvatarCallLatency;
  /** Smoothed mic input level, 0..1. Drives the live meter. */
  micLevel: number;
  /** True once the local mic track is published and being metered. */
  micActive: boolean;
  /** Resolved input-device label (e.g. "MacBook Pro Microphone"), when exposed. */
  micDeviceLabel: string | null;
  /** Selected input: SYSTEM_DEFAULT_MIC_ID, or a specific device id. */
  micDeviceId: string;
  /**
   * Pick the input device. Idle: remembered for the next call. Live: switches the
   * published track and re-points the meter at it.
   */
  selectMicDevice: (deviceId: string) => Promise<void>;
  /**
   * Half-duplex "speakers mode": when on, the mic is auto-muted while the avatar
   * is speaking so its voice (played over the speakers) can't leak back into the
   * mic and be transcribed as the user. Off by default.
   */
  speakersMode: boolean;
  /** Toggle {@link speakersMode}. Persists across a stop/start, like the device pick. */
  setSpeakersMode: (enabled: boolean) => void;
  /** True while the mic is muted — manually, or auto-muted by speakers mode. */
  micMuted: boolean;
  /** Flip the user's manual mute. Per-call state; a new call starts unmuted. */
  toggleMute: () => void;
  /** True once the <video> element has actually decoded an avatar frame. */
  videoReady: boolean;
  /** True when the browser is blocking avatar audio playback (autoplay policy). */
  audioBlocked: boolean;
  /** Retry audio playback from a user gesture ("Enable audio"). */
  enableAudio: () => Promise<void>;
  /** Start a call, minting the room token via the supplied thunk. */
  start: (mint: MintRoomToken) => Promise<void>;
  stop: () => void;
}

interface UseAvatarCallOptions {
  /**
   * i18n key prefix under the `avatars` namespace for `.errors.*` labels.
   * `lab` = the sysadmin lab's diagnostic copy; `call` = the tenant's
   * user-facing copy. Defaults to `call`.
   */
  tPrefix?: string;
}

export function useAvatarCall(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  audioRef: React.RefObject<HTMLAudioElement | null>,
  { tPrefix = 'call' }: UseAvatarCallOptions = {},
): UseAvatarCall {
  const { t } = useTranslation('avatars');
  const [status, setStatus] = useState<AvatarCallStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<AvatarCallTranscriptLine[]>([]);
  const [latency, setLatency] = useState<AvatarCallLatency>({});
  const [micLevel, setMicLevel] = useState(0);
  const [micActive, setMicActive] = useState(false);
  const [micDeviceLabel, setMicDeviceLabel] = useState<string | null>(null);
  const [micDeviceId, setMicDeviceId] = useState<string>(SYSTEM_DEFAULT_MIC_ID);
  const [videoReady, setVideoReady] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [speakersMode, setSpeakersModeState] = useState(false);
  const [micMuted, setMicMuted] = useState(false);

  const roomRef = useRef<Room | null>(null);
  const connectedAtRef = useRef<number | null>(null);
  const gotFirstFrameRef = useRef(false);
  const agentDeadlineRef = useRef<number | null>(null);
  // Bumped on every teardown/stop/new-start; a running start() captures its value
  // and bails after each await once it no longer matches (this attempt superseded).
  const startGenRef = useRef(0);
  const videoTrackRef = useRef<RemoteVideoTrack | null>(null);
  const audioTrackRef = useRef<RemoteAudioTrack | null>(null);
  const micMeterRef = useRef<{ interval: number; cleanup: () => Promise<void> } | null>(null);
  const micTrackRef = useRef<LocalAudioTrack | null>(null);
  const micDeviceIdRef = useRef<string>(SYSTEM_DEFAULT_MIC_ID);
  const micSelectGenRef = useRef(0);
  const speakersModeRef = useRef(false);
  const agentSpeakingRef = useRef(false);
  const micMutedRef = useRef(false);
  // The user's explicit mute toggle. Distinct from the speakers-mode AUTO-mute:
  // manual mute holds regardless of who is speaking, and both feed the same
  // reconciler below. Per-call state — a new call always starts unmuted.
  const manualMuteRef = useRef(false);

  /**
   * Reconciles the published mic against mute state: the mic is muted iff the
   * user muted it manually OR speakers mode is on AND the avatar is speaking.
   * Idempotent.
   */
  const applyHalfDuplex = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    const shouldMute =
      manualMuteRef.current || (speakersModeRef.current && agentSpeakingRef.current);
    if (shouldMute === micMutedRef.current) return;
    micMutedRef.current = shouldMute;
    setMicMuted(shouldMute);
    void room.localParticipant.setMicrophoneEnabled(!shouldMute);
  }, []);

  const setSpeakersMode = useCallback(
    (enabled: boolean) => {
      speakersModeRef.current = enabled;
      setSpeakersModeState(enabled);
      applyHalfDuplex();
    },
    [applyHalfDuplex],
  );

  /**
   * Flips the user's manual mute, reconciled through the same idempotent path as
   * the speakers-mode auto-mute (so the two can't fight over the track).
   */
  const toggleMute = useCallback(() => {
    manualMuteRef.current = !manualMuteRef.current;
    applyHalfDuplex();
  }, [applyHalfDuplex]);

  const stopMicMeter = useCallback(() => {
    const meter = micMeterRef.current;
    micMeterRef.current = null;
    if (meter) {
      window.clearInterval(meter.interval);
      void meter.cleanup();
    }
    setMicLevel(0);
    setMicActive(false);
    setMicDeviceLabel(null);
  }, []);

  const clearAgentDeadline = useCallback(() => {
    if (agentDeadlineRef.current !== null) {
      window.clearTimeout(agentDeadlineRef.current);
      agentDeadlineRef.current = null;
    }
  }, []);

  const attachVideo = useCallback(() => {
    const track = videoTrackRef.current;
    const el = videoRef.current;
    if (track && el) track.attach(el);
  }, [videoRef]);

  const attachAudio = useCallback(() => {
    const track = audioTrackRef.current;
    const el = audioRef.current;
    if (!track || !el) return;
    track.attach(el);
    // A muted sink is the classic "avatar is silent but everything looks fine" bug.
    el.muted = false;
    el.volume = 1;
  }, [audioRef]);

  const teardown = useCallback(() => {
    startGenRef.current += 1; // invalidate any in-flight start()
    clearAgentDeadline();
    stopMicMeter();
    videoTrackRef.current?.detach();
    audioTrackRef.current?.detach();
    videoTrackRef.current = null;
    audioTrackRef.current = null;
    micTrackRef.current = null;
    agentSpeakingRef.current = false;
    micMutedRef.current = false;
    manualMuteRef.current = false;
    setMicMuted(false);
    const room = roomRef.current;
    roomRef.current = null;
    connectedAtRef.current = null;
    gotFirstFrameRef.current = false;
    setVideoReady(false);
    setAudioBlocked(false);
    if (room) {
      void room.disconnect();
    }
  }, [clearAgentDeadline, stopMicMeter]);

  const stop = useCallback(() => {
    teardown();
    setStatus('idle');
    setErrorMessage(null);
  }, [teardown]);

  const enableAudio = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const myGen = startGenRef.current;
    await room.startAudio();
    if (startGenRef.current !== myGen) return;
    attachAudio();
    setAudioBlocked(!room.canPlaybackAudio);
  }, [attachAudio]);

  // `videoReady` is driven by the element actually decoding a frame — not by the
  // track being subscribed. `loadeddata` fires at readyState >= HAVE_CURRENT_DATA.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const onFrame = () => {
      if (roomRef.current === null) return;
      setVideoReady(true);
      if (connectedAtRef.current !== null) {
        const elapsed = Math.round(performance.now() - connectedAtRef.current);
        setLatency((prev) =>
          prev.firstFrameMs === undefined ? { ...prev, firstFrameMs: elapsed } : prev,
        );
      }
    };
    el.addEventListener('loadeddata', onFrame);
    el.addEventListener('playing', onFrame);
    return () => {
      el.removeEventListener('loadeddata', onFrame);
      el.removeEventListener('playing', onFrame);
    };
  }, [videoRef]);

  const startMicMeter = useCallback(
    (micTrack: LocalAudioTrack, myGen: number) => {
      stopMicMeter();
      const { calculateVolume, cleanup } = createAudioAnalyser(micTrack);
      const interval = window.setInterval(() => {
        if (startGenRef.current !== myGen) return;
        const volume = calculateVolume();
        setMicLevel((prev) =>
          prev + (Math.min(1, Math.max(0, volume)) - prev) * MIC_LEVEL_SMOOTHING,
        );
      }, MIC_METER_INTERVAL_MS);
      micMeterRef.current = { interval, cleanup };
      setMicActive(true);
      setMicDeviceLabel(micTrack.mediaStreamTrack?.label || null);
    },
    [stopMicMeter],
  );

  const switchLiveMicDevice = useCallback(
    async (deviceId: string, previous: string, selectGen: number) => {
      const room = roomRef.current;
      const micTrack = micTrackRef.current;
      if (!room || !micTrack) return;

      const myGen = startGenRef.current;
      const isCurrent = () =>
        startGenRef.current === myGen && micSelectGenRef.current === selectGen;

      try {
        await room.switchActiveDevice(
          'audioinput',
          deviceId === SYSTEM_DEFAULT_MIC_ID ? BROWSER_DEFAULT_DEVICE_ID : deviceId,
        );
      } catch (err) {
        if (!isCurrent()) return;
        // eslint-disable-next-line no-restricted-syntax -- bare non-mutation call, no global handler fires
        toast.error(t(`${tPrefix}.errors.${micFailureKey(err)}`));
        if (deviceId !== SYSTEM_DEFAULT_MIC_ID) {
          micDeviceIdRef.current = previous;
          setMicDeviceId(previous);
        }
        return;
      }
      if (!isCurrent()) return;
      startMicMeter(micTrack, myGen);
    },
    [startMicMeter, t, tPrefix],
  );

  const selectMicDevice = useCallback(
    async (deviceId: string) => {
      const previous = micDeviceIdRef.current;
      if (deviceId === previous) return;
      micDeviceIdRef.current = deviceId;
      setMicDeviceId(deviceId);
      micSelectGenRef.current += 1;
      await switchLiveMicDevice(deviceId, previous, micSelectGenRef.current);
    },
    [switchLiveMicDevice],
  );

  const start = useCallback(
    async (mint: MintRoomToken) => {
      // Reset any prior call (teardown bumps the generation); capture ours.
      teardown();
      const myGen = startGenRef.current;
      setErrorMessage(null);
      setTranscript([]);
      setLatency({});
      setStatus('connecting');

      const failAgentStartup = (key: AgentFailureKey) => {
        teardown();
        setErrorMessage(t(`${tPrefix}.errors.${key}`));
        setStatus('error');
      };
      const failMic = (key: MicFailureKey) => {
        teardown();
        setErrorMessage(t(`${tPrefix}.errors.${key}`));
        setStatus('error');
      };
      const armAgentDeadline = (ms: number, key: AgentFailureKey) => {
        clearAgentDeadline();
        agentDeadlineRef.current = window.setTimeout(() => {
          agentDeadlineRef.current = null;
          if (startGenRef.current !== myGen || gotFirstFrameRef.current) return;
          failAgentStartup(key);
        }, ms);
      };

      let livekitUrl: string;
      let roomToken: string;
      try {
        const tokenStart = performance.now();
        const resp = await mint();
        livekitUrl = resp.livekit_url;
        roomToken = resp.token;
        if (startGenRef.current !== myGen) return;
        setLatency({ tokenMs: Math.round(performance.now() - tokenStart) });
      } catch (err) {
        if (startGenRef.current !== myGen) return;
        // Bare (non-React-Query) call — no global mutation toast fires here, so
        // this is the one local toast (ui-form-errors.md §3c). getErrorMessage
        // translates the backend code (403, 503 LIVEKIT_NOT_CONFIGURED, 429
        // concurrency, AI_SUSPENDED, …) both mint endpoints can return.
        const message = getErrorMessage(err);
        // eslint-disable-next-line no-restricted-syntax -- bare non-mutation call, no global handler fires
        toast.error(message);
        setErrorMessage(message);
        setStatus('error');
        return;
      }

      try {
        const chosenDeviceId = micDeviceIdRef.current;
        const room = new Room({
          adaptiveStream: true,
          audioCaptureDefaults: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            ...(chosenDeviceId !== SYSTEM_DEFAULT_MIC_ID && { deviceId: chosenDeviceId }),
          },
        });
        roomRef.current = room;

        room.on(
          RoomEvent.TrackSubscribed,
          (track: RemoteTrack, _pub: RemoteTrackPublication, _p: RemoteParticipant) => {
            if (track.kind === Track.Kind.Video) {
              clearAgentDeadline(); // avatar video is up — startup succeeded
              videoTrackRef.current = track as RemoteVideoTrack;
              attachVideo();
              gotFirstFrameRef.current = true;
            } else if (track.kind === Track.Kind.Audio) {
              audioTrackRef.current = track as RemoteAudioTrack;
              attachAudio();
              setAudioBlocked(!room.canPlaybackAudio);
            }
          },
        );

        room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
          if (startGenRef.current !== myGen) return;
          setAudioBlocked(!room.canPlaybackAudio);
        });

        room.on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
          if (startGenRef.current !== myGen) return;
          agentSpeakingRef.current = speakers.some((s) => s !== room.localParticipant);
          applyHalfDuplex();
        });

        room.on(RoomEvent.ParticipantConnected, () => {
          if (startGenRef.current !== myGen || gotFirstFrameRef.current) return;
          armAgentDeadline(AGENT_VIDEO_TIMEOUT_MS, 'agentFailed');
        });

        room.on(RoomEvent.ParticipantDisconnected, () => {
          if (startGenRef.current !== myGen || gotFirstFrameRef.current) return;
          // The Beyond Presence topology has TWO agent-side participants (the
          // worker agent and a bey-avatar-agent that briefly drops/rejoins during
          // setup), so a single leave is not a crash — only an empty room is.
          if (room.remoteParticipants.size === 0) {
            failAgentStartup('agentFailed');
          }
        });

        room.on(
          RoomEvent.DataReceived,
          (payload: Uint8Array, _p?: RemoteParticipant, _kind?: unknown, topic?: string) => {
            if (startGenRef.current !== myGen || topic !== AVATAR_ERROR_TOPIC) return;
            const parsed = parseAvatarProviderError(payload);
            teardown();
            setErrorMessage(
              parsed
                ? t(
                    parsed.code === AVATAR_PROVIDER_AT_CAPACITY
                      ? `${tPrefix}.errors.providerAtCapacity`
                      : `${tPrefix}.errors.providerStartFailed`,
                    {
                      errorId: parsed.error_id ?? '',
                      timestamp: parsed.timestamp ?? `${new Date().toISOString().slice(0, 19)}Z`,
                      defaultValue: parsed.message,
                    },
                  )
                : getGenericErrorMessage(),
            );
            setStatus('error');
          },
        );

        room.on(
          RoomEvent.TranscriptionReceived,
          (segments: TranscriptionSegment[], participant?: Participant) => {
            const speaker: AvatarCallTranscriptSpeaker =
              participant && participant === room.localParticipant ? 'user' : 'agent';
            setTranscript((prev) => {
              const next = [...prev];
              for (const seg of segments) {
                const idx = next.findIndex((l) => l.id === seg.id);
                const line = { id: seg.id, text: seg.text, final: seg.final, speaker };
                if (idx >= 0) next[idx] = line;
                else next.push(line);
              }
              return next;
            });
          },
        );

        room.on(RoomEvent.Disconnected, () => {
          if (roomRef.current === room) {
            teardown();
            setStatus('idle');
          }
        });

        const connectStart = performance.now();
        await room.connect(livekitUrl, roomToken);
        if (startGenRef.current !== myGen) {
          void room.disconnect();
          return;
        }
        setLatency((prev) => ({ ...prev, connectMs: Math.round(performance.now() - connectStart) }));
        connectedAtRef.current = performance.now();

        if (room.remoteParticipants.size > 0) {
          armAgentDeadline(AGENT_VIDEO_TIMEOUT_MS, 'agentFailed');
        } else {
          armAgentDeadline(AGENT_JOIN_TIMEOUT_MS, 'agentTimeout');
        }

        try {
          await room.startAudio();
        } catch {
          // Blocked despite the gesture (some Safari/iOS states). Not fatal —
          // canPlaybackAudio drives the explicit "Enable audio" control.
        }
        if (startGenRef.current !== myGen) {
          void room.disconnect();
          return;
        }
        setAudioBlocked(!room.canPlaybackAudio);
        attachAudio();

        let micPublication: LocalTrackPublication | undefined;
        try {
          micPublication = await room.localParticipant.setMicrophoneEnabled(true);
        } catch (err) {
          if (startGenRef.current !== myGen) return;
          failMic(micFailureKey(err));
          return;
        }
        if (startGenRef.current !== myGen) {
          void room.disconnect();
          return;
        }

        const micTrack = micPublication?.audioTrack as LocalAudioTrack | undefined;
        if (micTrack) {
          micTrackRef.current = micTrack;
          startMicMeter(micTrack, myGen);
        }
        setStatus('connected');

        if (micTrack && micDeviceIdRef.current !== chosenDeviceId) {
          micSelectGenRef.current += 1;
          await switchLiveMicDevice(
            micDeviceIdRef.current,
            chosenDeviceId,
            micSelectGenRef.current,
          );
        }
      } catch (err) {
        if (startGenRef.current !== myGen) return; // superseded call already cleaned up
        teardown();
        setErrorMessage(getGenericErrorMessage(err));
        setStatus('error');
      }
    },
    [teardown, clearAgentDeadline, attachVideo, attachAudio, startMicMeter, switchLiveMicDevice, applyHalfDuplex, t, tPrefix],
  );

  // Disconnect on unmount so a running call never outlives the page.
  useEffect(() => teardown, [teardown]);

  return {
    status,
    errorMessage,
    transcript,
    latency,
    micLevel,
    micActive,
    micDeviceLabel,
    micDeviceId,
    selectMicDevice,
    speakersMode,
    setSpeakersMode,
    micMuted,
    toggleMute,
    videoReady,
    audioBlocked,
    enableAudio,
    start,
    stop,
  };
}
